import { Fragment, useEffect, useState } from "react"
import { useSignInWithEmailAndPassword } from "react-firebase-hooks/auth"
import { verifyPasswordResetCode, confirmPasswordReset, applyActionCode, type Auth } from "firebase/auth"
import { auth } from "@utils/firebase"
import useQuery from "@utils/useQueryHook"
import ResetPassword from "@pages/authenticate/ResetPassword"
import { useNavigate } from "react-router-dom"
import Loading from "@components/layout/Loading"
import { useQueryClient } from "react-query"
import { FirebaseError } from "firebase/app"

export default function AuthActions() {
  const [loading, setLoading] = useState(true)
  const [processingReset, setProcessingReset] = useState(false)
  const query = useQuery()
  const navigateApi = useNavigate()
  const queryClient = useQueryClient()

  const [signInWithEmailAndPassword] = useSignInWithEmailAndPassword(auth)

  const mode = query.get("mode")
  const oobCode = query.get("oobCode")

  const [showResetPassword, setShowResetPassword] = useState(false)
  const [emailMessage, setEmailMessage] = useState("")
  const [errors, setErrors] = useState("")
  const [resetAttempts, setResetAttempts] = useState(0)

  const isFirebaseError = (error: unknown): error is FirebaseError => {
    return error instanceof FirebaseError
  }

  const getEmail = async () => {
    try {
      const email = await verifyPasswordResetCode(auth, oobCode as string)
      setEmailMessage(email)
      return email
    } catch (error) {
      console.error("Error verifying reset code:", error)

      // Handle specific error codes
      if (isFirebaseError(error)) {
        if (error.code === "auth/expired-action-code") {
          setErrors("This password reset link has expired. Please request a new one.")
        } else if (error.code === "auth/invalid-action-code") {
          setErrors("This password reset link is invalid. Please request a new one.")
        } else {
          setErrors("Failed to verify reset code. Please try again or request a new password reset link.")
        }
      } else {
        setErrors("Failed to verify reset code. Please try again or request a new password reset link.")
      }

      return null
    }
  }

  const handleResetPassword = async (actionCode: string, password: string, confirmPassword: string) => {
    setProcessingReset(true)
    setLoading(true)
    setErrors("")

    try {
      if (password !== confirmPassword) {
        setErrors("Passwords do not match!")
        setProcessingReset(false)
        setLoading(false)
        return
      }

      // Verify the action code first
      const email = await verifyPasswordResetCode(auth, actionCode)

      if (!email) {
        setErrors("Invalid or expired reset link. Please request a new one.")
        setProcessingReset(false)
        setLoading(false)
        return
      }

      // Then reset the password
      await confirmPasswordReset(auth, actionCode, password)

      // Try to sign in with the new credentials
      try {
        await signInWithEmailAndPassword(email, password)
        // If successful, navigate to home
        navigateApi("/")
      } catch (signInErr) {
        console.error("Error signing in after password reset:", signInErr)

        // Even if sign-in fails, the password was reset successfully
        // Show success message and redirect to login
        setErrors("Password reset successful. Please sign in with your new password.")
        setTimeout(() => {
          navigateApi("/login")
        }, 3000)
      }
    } catch (error) {
      console.error("Error during password reset:", error)
      setResetAttempts((prev) => prev + 1)

      if (isFirebaseError(error)) {
        if (error.code === "auth/weak-password") {
          setErrors("Password needs to be at least 6 characters long.")
        } else if (error.code === "auth/expired-action-code") {
          setErrors("This password reset link has expired. Please request a new one.")
        } else if (error.code === "auth/invalid-action-code") {
          setErrors("This password reset link is invalid. Please request a new one.")
        } else {
          setErrors("Failed to reset password. Please try again or request a new reset link.")
        }
      } else {
        setErrors("Failed to reset password. Please try again or request a new reset link.")
      }
    } finally {
      setProcessingReset(false)
      setLoading(false)
    }
  }

  const handleVerifyEmail = async (auth: Auth, actionCode: string) => {
    try {
      await applyActionCode(auth, actionCode)
      queryClient.invalidateQueries({ queryKey: ["user"] })
      window.location.href = "/verify/request-followup"
    } catch (error) {
      console.error("Error verifying email:", error)

      if (isFirebaseError(error)) {
        if (error.code === "auth/expired-action-code") {
          setErrors("This verification link has expired. Please request a new one.")
        } else if (error.code === "auth/invalid-action-code") {
          setErrors("This verification link is invalid. Please request a new one.")
        } else {
          setErrors("Failed to verify your email. Please try again later.")
        }
      } else {
        setErrors("Failed to verify your email. Please try again later.")
      }

      setLoading(false)
    }
  }

  useEffect(() => {
    const processAction = async () => {
      if (!oobCode) {
        setErrors("Invalid or missing action code.")
        setLoading(false)
        return
      }

      try {
        switch (mode) {
          case "resetPassword":
            // Display reset password handler and UI.
            const email = await getEmail()
            if (email) {
              setShowResetPassword(true)
            }
            setLoading(false)
            break
          case "verifyEmail":
            // Display email verification handler and UI.
            await handleVerifyEmail(auth, oobCode)
            break
          default:
            setErrors("Invalid action. Please try again.")
            setLoading(false)
        }
      } catch (error) {
        console.error("Error processing action:", error)
        setErrors("An error occurred. Please try again later.")
        setLoading(false)
      }
    }

    processAction()
  }, [mode, oobCode])

  if (loading && !processingReset) {
    return (
      <Fragment>
        <Loading messages={{ title: "Loading...", subtitle: "We are verifying" }} />
      </Fragment>
    )
  }

  if (errors && !showResetPassword) {
    return (
      <div className="w-full grow flex items-center justify-center">
        <div className="max-w-full md:max-w-[400px] md:my-4 w-full py-10 mx-auto md:border md:border-pn_gray-200 bg-pn_paper-1 rounded-3xl">
          <div className="p-6">
            <div className="bg-pn_red-50 border border-pn_red-500 text-pn_red-500 p-4 rounded-lg text-center mb-4">
              <p className="text-pn-body-emphasis">{errors}</p>
            </div>

            <div className="flex flex-col gap-4">
              <button onClick={() => navigateApi("/forgot-password")} className="btn-primary-lg">
                Request New Reset Link
              </button>

              <button onClick={() => navigateApi("/login")} className="btn-secondary-lg">
                Back to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (showResetPassword && oobCode) {
    return (
      <ResetPassword email={emailMessage} onSubmit={(password) => handleResetPassword(oobCode, password, password)} />
    )
  }

  return null
}
